export const MEMBER_TYPE = {
  INVITATION: 'INVITATION',
  USER: 'USER',
} as const;

export type InvitationOrUser = typeof MEMBER_TYPE[keyof typeof MEMBER_TYPE];

export const LIMITED_ROLE = {
  ADMIN: 'ADMIN',
  EMPLOYEE: 'EMPLOYEE',
} as const;

export type LimitedRole = typeof LIMITED_ROLE[keyof typeof LIMITED_ROLE];

export const SPECIAL_ADMIN_ROLES = {
  DC: 'DC',
  SUPPORT: 'SUPPORT',
} as const;

type SpecialAdminRole = typeof SPECIAL_ADMIN_ROLES[keyof typeof SPECIAL_ADMIN_ROLES];

export type Role = 'OWNER' | LimitedRole | SpecialAdminRole;

type UserRole = 'STANDARD' | 'SUPPORT_AGENT' | 'SUPPORT_MANAGER' | 'SWISS21_ADMIN' | 'TEACHER' | SpecialAdminRole;

export interface Me {
  email: string;
  firstName: string;
  gender: 'DIVERSE' | 'FEMALE' | 'MALE' | null;
  language: string;
  lastName: string;
  salutation: 'MR' | 'MR_AND_MRS' | 'MRS' | null;
  userRole: UserRole;
  uuid: string;
}

export type MePatchData = Partial<Pick<Me, 'gender' | 'salutation'>>;

export interface Account {
  accountRole: Role;
  availableUntil: string | null;
  email: string;
  isDemoAccount: boolean;
  isOwner: boolean;
  lastActivity: string;
  name: string;
  supportAvailableUntil: string | null;
  type: 'LESSON' | 'STANDARD' | 'TEMPLATE';
  uuid: string;
}

export interface SupportGrantedAccount extends Account {
  hasAccess: boolean;
}

export interface App {
  appKey: string;
  name: string;
}

interface UserBase {
  accountRole: Role;
  apps: App[];
  email: string;
  uuid: string;
}

export interface Invitation extends UserBase {
  accountName: string;
  accountUuid: string;
  createdAt: string;
  expiresAt: string;
}

export interface User extends UserBase {
  firstName: string;
  /** @deprecated */
  isOwner: boolean;
  language: string;
  lastLoginAt: string | null;
  lastName: string;
  oauthUuid: string;
}

export type UserOrInvitation =
  | (Invitation & { type: typeof MEMBER_TYPE.INVITATION })
  | (User & { type: typeof MEMBER_TYPE.USER });

export type UserRequest = Omit<User, 'apps' | 'isOwner' | 'language' | 'lastLoginAt' | 'oauthUuid' | 'uuid'>
  & { apps: string[] };

export type InvitationRequest = Omit<Invitation, 'apps' | 'uuid'> & { apps: string[] };

export interface UpdateInvitationPayload {
  accountUuid: string;
  invitationUuid: string;
}
